import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { RouteDecisionSkeleton } from '@/components/feedback/route-decision-skeleton';
import { OnboardingShell, StepTitle } from '@/components/onboarding/onboarding-shell';
import {
    draftFromUser,
    draftToPatch,
    isDraftComplete,
    ProfessionalProfileFields,
    type IProfessionalProfileDraft,
} from '@/components/profile/professional-profile-fields';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/context';
import { useCurrentTenant } from '@/context/tenant-registry-context';
import { useCurrentUser, useOnboardingState, useUpdateProfile } from '@/hooks/use-current-user';
import { useEmailVerificationState } from '@/hooks/use-email-verification-state';
import { useRegions } from '@/hooks/use-regions';
import { useCurrentTenantDetails } from '@/hooks/use-tenants';
import { errorIds, logError } from '@/lib/log-error';

/**
 * The professional profile as a standalone page after activation, and the
 * landing page of an invited member whose profile is incomplete (ESCLOUD-333).
 * No stepper: an invited member never walked the subscription steps.
 *
 * Completeness is the server's answer (`GET /users/me/onboarding-state`); a
 * profile the server already calls complete passes straight through, so a
 * returning buyer creating a second workspace is not asked twice.
 */
export function WelcomeProfilePage() {
    const navigate = useNavigate();
    const { tenantId } = useTenant();
    const { entry } = useCurrentTenant();
    const tenantQuery = useCurrentTenantDetails();
    const userQuery = useCurrentUser();
    const onboardingQuery = useOnboardingState();
    const updateProfile = useUpdateProfile();
    const emailState = useEmailVerificationState();
    const regions = useRegions();

    const destination = tenantId ? `/tenants/${tenantId}` : '/';
    const tenant = tenantQuery.data;
    const institutionFallback = tenant?.type === 'organization' ? tenant.name : '';

    const [draft, setDraft] = useState<IProfessionalProfileDraft>(() => draftFromUser(undefined, ''));
    // The instance mounts while the user query is still pending (the skeleton
    // below is what is shown), so the useState initializer above only ever
    // sees `undefined`. The institution fallback comes from `useCurrentTenantDetails`,
    // a query independent from the user query, so the user query settling is not
    // enough: seeding then would latch an empty institution before the tenant
    // details arrive. Wait for both, but only for tenant details when there is a
    // tenant in context; an errored tenant query counts as settled and seeds
    // without a fallback. Seed once, and never again: an edit in progress must
    // not be clobbered by a later answer.
    const [seeded, setSeeded] = useState(false);
    useEffect(() => {
        if (seeded || userQuery.isPending) return;
        if (tenantId && tenantQuery.isPending) return;
        setDraft(draftFromUser(userQuery.data, institutionFallback));
        setSeeded(true);
    }, [seeded, userQuery.isPending, userQuery.data, tenantId, tenantQuery.isPending, institutionFallback]);

    if (userQuery.isPending || onboardingQuery.isPending) {
        return <RouteDecisionSkeleton />;
    }

    if (onboardingQuery.data?.profileComplete) {
        return <Navigate to={destination} replace />;
    }

    // No editable form before the seed above has run: it replaces the whole
    // draft, so anything typed while the tenant details were pending was lost.
    if (!seeded) {
        return <RouteDecisionSkeleton />;
    }

    const isOwner = entry?.role === 'owner';
    const regionName = entry
        ? (regions.data?.items.find((r) => r.code === entry.tenant.region)?.name ??
          entry.tenant.region.toUpperCase())
        : null;
    const email = userQuery.data?.email;
    const saving = updateProfile.isPending;

    const submit = async () => {
        try {
            await updateProfile.mutateAsync(draftToPatch(draft));
            navigate(destination, { replace: true });
        } catch (error) {
            logError(errorIds.PROFILE_SAVE_FAILED, 'Welcome profile submission failed', { error });
            toast.error('Your profile could not be saved', {
                description: 'Nothing was lost. Please try again.',
            });
        }
    };

    return (
        <OnboardingShell
            footer={
                <>
                    <span className="text-[13px] text-muted-foreground">
                        You can change this later in Settings.
                    </span>
                    <Button
                        size="lg"
                        onClick={() => void submit()}
                        disabled={!isDraftComplete(draft) || saving}
                    >
                        {saving ? 'Saving…' : 'Open ESCloud'}
                    </Button>
                </>
            }
        >
            <StepTitle
                title="Your professional profile"
                sub="Appears on exported reports and in the audit trail. You can change it later in Settings."
            />
            <div className="rounded-xl border border-border bg-card p-5">
                <ProfessionalProfileFields
                    value={draft}
                    onChange={setDraft}
                    idPrefix="welcome"
                    disabled={saving}
                />
            </div>
            <p
                role="status"
                className="mt-4 rounded-xl border border-border bg-card p-4 text-[13px] leading-relaxed text-muted-foreground"
            >
                {isOwner ? (
                    <>
                        Your subscription is active.
                        {regionName && (
                            <>
                                {' '}
                                Data region:{' '}
                                <span className="font-semibold text-foreground">{regionName}</span>.
                            </>
                        )}
                        {emailState === 'unverified' && email && (
                            <>
                                {' '}
                                A verification email has been sent to {email}. You can continue without
                                verifying now.
                            </>
                        )}
                    </>
                ) : (
                    <>
                        You have joined{' '}
                        <span className="font-semibold text-foreground">
                            {entry?.tenant.name ?? 'your organization'}
                        </span>
                        .
                    </>
                )}
            </p>
        </OnboardingShell>
    );
}
